import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BACKEND_URL } from '../app.config';
import { ListResult, Form } from '../api/data-model';
import { FormField, Transform } from '../api/forms/form-field';
import { ApplyCopiedFormToWorkflowsDto, CopyFieldMappingsResult, CopyQuestionnaire, CopyQuestionnaireCopyRequest, CopyQuestionnaireCopyResult, FormCopyWorkflow} from '../components/admin/form/copy-form/copy-form';

@Injectable({
  providedIn: 'root'
})
export class FormService {

  constructor(private readonly http: HttpClient, @Inject(BACKEND_URL) private readonly baseUrl: string) {

  }

  getList(sorting: string, skipCount: number, maxResultCount: number): Observable<ListResult<Form>> {
    return this.http.get<ListResult<Form>>(this.baseUrl + '/api/app/form', {
      params: {
        sorting,
        skipCount,
        maxResultCount,
      },
    });
  }

  getForm(id: string): Observable<Form> {
    return this.http.get<Form>(this.baseUrl + '/api/app/form/' + id);
  }

  getQuestionnaires(id: string): Observable<CopyQuestionnaire[]> {
    return this.http.get<CopyQuestionnaire[]>(
      `${this.baseUrl}/api/app/form/${id}/questionnaires`
    );
  }

  getCopyWorkflows(id: string): Observable<FormCopyWorkflow[]> {
    return this.http.get<FormCopyWorkflow[]>(
      `${this.baseUrl}/api/app/form/${id}/copy-workflows`
    );
  }

  applyToWorkflows(id: string, input: ApplyCopiedFormToWorkflowsDto): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/api/app/form/${id}/apply-to-workflows`, input);
  }

  copyQuestionnaires(input: CopyQuestionnaireCopyRequest): Observable<CopyQuestionnaireCopyResult> {
    return this.http.post<CopyQuestionnaireCopyResult>(
      `${this.baseUrl}/api/app/questionnaire/copy`,
      input
    );
  }

  addForm(form: Form): Observable<Form> {
    return this.http.post<Form>(`${this.baseUrl}/api/app/form`, form);
  }

  updateForm(id: string, form: Form): Observable<Form> {
    return this.http.put<Form>(`${this.baseUrl}/api/app/form/${id}`, form);
  }

  completeCopy(id: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/api/app/form/${id}/complete-copy`, null);
  }

  updateCopyStep(id: string, stepNo: number): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/api/app/form/${id}/copy-step`, null, {
      params: { stepNo },
    });
  }

  getFields(id: string): Observable<FormField[]> {
    return this.http.get<FormField[]>(this.baseUrl + '/api/app/form/' + id + '/fields');
  }

  updateFields(id: string, fields: FormField[], filePath: string | null, issues: number | null): Observable<FormField[]> {
    return this.http.put<FormField[]>(
      `${this.baseUrl}/api/app/form/${id}/fields`,
      {
        formFields: fields.map(f => ({ ...f, isMultiField: f.fieldKeys.length > 1 })),
        filePath: filePath,
        issues: issues
      });
  }

  getFormFileBlob(id: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/api/app/form/${id}/file`, {
      responseType: 'blob'
    });
  }

  uploadFormFile(id: string, file: Blob, filename: string): Observable<any> {
    const formData = new FormData();
    formData.append('FormFile', file, filename);
    return this.http.post(`${this.baseUrl}/api/app/form/${id}/upload`, formData);
  }
  uploadPDFFormDuringCopy(id: string, file: Blob, filename: string): Observable<any> {
    const formData = new FormData();
    formData.append('FormFile', file, filename);
    return this.http.post(`${this.baseUrl}/api/app/form/${id}/upload-and-save`, formData);
  }

  getTransforms(): Observable<Transform[]> {
    return this.http.get<Transform[]>(`${this.baseUrl}/api/app/form/transforms`);
  }

  copyFieldMapping(id: string, fieldMapping: unknown): Observable<CopyFieldMappingsResult> {
    return this.http.post<CopyFieldMappingsResult>(
      `${this.baseUrl}/api/app/form/${id}/copy-field-mappings`,
      fieldMapping
    );
  }
}
